import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useCountries } from '../../hooks/useCountries';
import { API_BASE_URL } from '../../config/api';
import { getDeliverLocal, setDeliverLocal } from '../../utils/mobileSettingsStorage';
import DeliverToModal from './DeliverToModal';
import RateAppModal from './RateAppModal';
import './MobileSettingsPage.css';

const APP_VERSION = '1.0.0';

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const DEFAULT_LANGS_FALLBACK = [
  { name: 'English', value: 'en' },
  { name: '简体中文', value: 'zh' },
  { name: 'Español', value: 'es' },
  { name: 'Русский', value: 'ru' },
  { name: 'Türkçe', value: 'tr' },
  { name: 'Português', value: 'pt' },
  { name: 'Italiano', value: 'it' },
  { name: 'Français', value: 'fr' },
  { name: 'Deutsch', value: 'de' },
  { name: 'Nederlands', value: 'nl' },
  { name: 'ไทย', value: 'th' },
  { name: '日本語', value: 'ja' },
  { name: 'Tiếng Việt', value: 'vi' },
  { name: '한국어', value: 'ko' },
  { name: 'Bahasa Indonesia', value: 'id' },
  { name: 'العربية', value: 'ar' }
];

export default function MobileSettingsPage() {
  const navigate = useNavigate();
  const { i18n: i18nInstance } = useTranslation();
  const { user, logout, updateProfile } = useAuth();
  const { selectedCurrency } = useCurrency();
  const { countries, loading: countriesLoading } = useCountries();
  const [cacheCleared, setCacheCleared] = useState(false);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [rateConfig, setRateConfig] = useState(null);
  const [langOptions, setLangOptions] = useState([]);

  const loadLangOptions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/settings/options/public/app_language`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const list = (data || [])
        .map((o) => ({ name: o.name, value: o.value || o.name, sortOrder: o.sortOrder || 0 }))
        .filter((o) => o.name && o.value);
      if (list.length) {
        setLangOptions(list.sort((a, b) => a.sortOrder - b.sortOrder));
        return;
      }
    } catch (_) {}
    setLangOptions(DEFAULT_LANGS_FALLBACK);
  }, []);

  useEffect(() => {
    loadLangOptions();
  }, [loadLangOptions]);

  const openRateModal = useCallback(async () => {
    setRateOpen(true);
    setRateConfig(null);
    try {
      const res = await fetch(`${API_BASE_URL}/settings/rate-app/public`);
      if (res.ok) {
        setRateConfig(await res.json());
      } else {
        setRateConfig({});
      }
    } catch (_) {
      setRateConfig({});
    }
  }, []);

  const closeRateModal = useCallback(() => {
    setRateOpen(false);
    setRateConfig(null);
  }, []);

  const handleRateIssue = useCallback(() => {
    const raw = (rateConfig && rateConfig.feedbackPath) || '/mobile/help-centre';
    closeRateModal();
    if (/^https?:\/\//i.test(raw)) {
      window.location.href = raw;
      return;
    }
    navigate(raw.startsWith('/') ? raw : `/${raw}`);
  }, [rateConfig, closeRateModal, navigate]);

  const localDeliver = getDeliverLocal();

  const deliverState = useMemo(() => {
    if (user?.countryCode) {
      const c = countries.find((x) => x.code === user.countryCode);
      if (c) {
        return {
          code: c.code,
          name: c.name,
          flag: c.flag,
          postal: user.deliveryPostalCode || localDeliver?.postal || ''
        };
      }
    }
    if (user?.country && countries.length) {
      const c = countries.find(
        (x) => (x.name || '').toLowerCase() === String(user.country).toLowerCase()
      );
      if (c) {
        return {
          code: c.code,
          name: c.name,
          flag: c.flag,
          postal: user.deliveryPostalCode || localDeliver?.postal || ''
        };
      }
    }
    if (localDeliver?.countryCode && countries.length) {
      const c = countries.find((x) => x.code === localDeliver.countryCode);
      if (c) {
        return {
          code: c.code,
          name: c.name,
          flag: c.flag,
          postal: localDeliver.postal || ''
        };
      }
    }
    if (countries.length) {
      const c = countries.find((x) => x.code === 'IN') || countries[0];
      return {
        code: c.code,
        name: c.name,
        flag: c.flag,
        postal: ''
      };
    }
    return { code: 'IN', name: 'India', flag: null, postal: '' };
  }, [user, localDeliver, countries]);

  const languageLabel = useMemo(() => {
    const key = (user && user.appLanguage) || i18nInstance.language || 'en';
    const code = (key || 'en').split('-')[0];
    const opt = (langOptions.length ? langOptions : DEFAULT_LANGS_FALLBACK).find(
      (l) => l.value === code
    );
    return opt ? opt.name : 'English';
  }, [user, langOptions, i18nInstance.language]);

  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  const handleClearCache = () => {
    try {
      const keysToKeep = ['token', 'authToken'];
      const allKeys = Object.keys(localStorage);
      allKeys.forEach((key) => {
        if (!keysToKeep.includes(key)) localStorage.removeItem(key);
      });
      sessionStorage.clear();
    } catch (_) {}
    setCacheCleared(true);
    setTimeout(() => setCacheCleared(false), 2000);
  };

  const handleConfirmDeliver = async ({ countryCode, postal }) => {
    const c = countries.find((x) => x.code === countryCode);
    const name = c ? c.name : countryCode;
    setDeliverLocal({ countryCode, name, postal: postal || '' });
    if (user) {
      await updateProfile({
        country: name,
        countryCode,
        deliveryPostalCode: postal || null
      });
    }
    setDeliverOpen(false);
  };

  return (
    <div className="mset-page">
      <div className="mset-header">
        <button className="mset-back-btn" onClick={() => navigate(-1)} aria-label="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span className="mset-title">Settings</span>
      </div>

      <div className="mset-body">
        {user && (
          <div className="mset-section">
            <button className="mset-row" onClick={() => navigate('/mobile/profile')}>
              <span className="mset-row-label">My profile</span>
              <ChevronRight />
            </button>
          </div>
        )}

        <div className="mset-section">
          <button className="mset-row" onClick={() => setDeliverOpen(true)}>
            <span className="mset-row-label">Deliver to</span>
            <span className="mset-row-right">
              {deliverState.flag ? (
                <img src={deliverState.flag} alt="" className="mset-row-flag" />
              ) : null}
              <span className="mset-row-value">
                {deliverState.name} ({deliverState.code})
              </span>
              <ChevronRight />
            </span>
          </button>

          <div className="mset-divider" />

          <button className="mset-row" onClick={() => navigate('/mobile/settings/currency')}>
            <span className="mset-row-label">Currency</span>
            <span className="mset-row-right">
              <span className="mset-row-value">{selectedCurrency?.code || 'USD'}</span>
              <ChevronRight />
            </span>
          </button>

          <div className="mset-divider" />

          <button className="mset-row" onClick={() => navigate('/mobile/settings/language')}>
            <span className="mset-row-label">Language</span>
            <span className="mset-row-right">
              <span className="mset-row-value">{languageLabel}</span>
              <ChevronRight />
            </span>
          </button>

          <div className="mset-divider" />

          <button className="mset-row" onClick={() => navigate('/mobile/notification-settings')}>
            <span className="mset-row-label">Notifications</span>
            <ChevronRight />
          </button>

          <div className="mset-divider" />

          <button className="mset-row" onClick={() => navigate('/profile')}>
            <span className="mset-row-label">Change password</span>
            <ChevronRight />
          </button>

          <div className="mset-divider" />

          <button className="mset-row" onClick={() => navigate('/profile')}>
            <span className="mset-row-label">Update email address</span>
            <ChevronRight />
          </button>

          <div className="mset-divider" />

          <button className="mset-row mset-row-danger" onClick={() => {}}>
            <span className="mset-row-label">Delete account</span>
            <ChevronRight />
          </button>

          <div className="mset-divider" />

          <button className="mset-row" onClick={() => {}}>
            <span className="mset-row-label">Network acceleration</span>
            <ChevronRight />
          </button>
        </div>

        <div className="mset-section mset-section-gray">
          <div className="mset-version-row">
            <div className="mset-version-left">
              <span className="mset-version-label">Version</span>
              <span className="mset-version-num">{APP_VERSION}</span>
            </div>
            <button className="mset-update-btn">UPDATE</button>
          </div>
        </div>

        <div className="mset-section">
          <button className="mset-row" onClick={() => navigate('/mobile/legal')}>
            <span className="mset-row-label">Legal policies</span>
            <ChevronRight />
          </button>

          <div className="mset-divider" />

          <button className="mset-row" onClick={openRateModal}>
            <span className="mset-row-label">Rate the app</span>
            <ChevronRight />
          </button>

          <div className="mset-divider" />

          <button className="mset-row" onClick={handleClearCache}>
            <span className="mset-row-label">
              Clear app cache
              {cacheCleared && <span className="mset-cache-ok"> ✓ Cleared</span>}
            </span>
            <ChevronRight />
          </button>
        </div>

        <div className="mset-section">
          <button className="mset-row" onClick={() => {}}>
            <span className="mset-row-label">Switch account</span>
            <span className="mset-row-right">
              <span className="mset-switch-badge">0</span>
              <ChevronRight />
            </span>
          </button>
        </div>

        <div className="mset-signout-wrap">
          <button className="mset-signout-btn" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </div>

      <DeliverToModal
        open={deliverOpen}
        onClose={() => setDeliverOpen(false)}
        onConfirm={handleConfirmDeliver}
        countries={countries}
        countriesLoading={countriesLoading}
        countryCode={deliverState.code}
        postal={deliverState.postal}
      />
      <RateAppModal
        open={rateOpen}
        onClose={closeRateModal}
        config={rateConfig}
        onIssue={handleRateIssue}
      />
    </div>
  );
}
